/**
 * Aba de depuração "ver como cliente" (spec 020) — o estado da aba.
 *
 * O token chega pelo FRAGMENTO da URL (`/portal#impersonation=<token>`), que
 * nenhuma requisição leva ao servidor, e vai para o sessionStorage DESTA aba.
 * A página o tira da URL (history.replaceState) antes de qualquer requisição
 * (FR-007) — `captureImpersonationFromLocation` roda no primeiro módulo que o
 * main.tsx avalia.
 *
 * Por que sessionStorage e nunca localStorage: o localStorage é da ORIGEM, e é
 * lá que mora a sessão do admin, aberta na outra aba. Se a aba de depuração
 * escrevesse nele, o admin passaria a ser a Maria; se lesse, a aba de depuração
 * cairia na sessão do admin assim que a de depuração acabasse. Por isso o modo
 * da página é decidido UMA vez, no carregamento, e não muda até recarregar.
 */
import { useSyncExternalStore } from 'react';
import { IMPERSONATION_FRAGMENT_KEY } from '@erp/shared';

const TOKEN_KEY = 'erp.impersonation.token';
export const IMPERSONATION_USER_KEY = 'erp.impersonation.user';
const ENDED_KEY = 'erp.impersonation.ended';

/** `ended` = Encerrar nesta aba; `expired` = o prazo acabou; `revoked` = a API recusou (401) */
export type ImpersonationEndReason = 'ended' | 'expired' | 'revoked';

export type ImpersonationState =
  | { mode: 'none' }
  | { mode: 'active' }
  | { mode: 'ended'; reason: ImpersonationEndReason };

const JWT_SHAPE = /^[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+$/;
const END_REASONS: readonly ImpersonationEndReason[] = ['ended', 'expired', 'revoked'];

function read(key: string): string | null {
  try {
    return sessionStorage.getItem(key);
  } catch {
    return null;
  }
}

function write(key: string, value: string | null): void {
  try {
    if (value === null) sessionStorage.removeItem(key);
    else sessionStorage.setItem(key, value);
  } catch {
    /* sem storage: a sessão vale até recarregar a aba */
  }
}

/** decidido no carregamento; null = ainda não decidido */
let pageMode: boolean | null = null;

/**
 * Lê `#impersonation=<token>`, guarda na aba e LIMPA A URL. Tem de rodar antes
 * de qualquer requisição e antes do router ler a location.
 */
export function captureImpersonationFromLocation(): void {
  const hash = window.location.hash.replace(/^#/, '');
  const token = hash ? new URLSearchParams(hash).get(IMPERSONATION_FRAGMENT_KEY) : null;
  if (token !== null) {
    // primeiro some da barra de endereço e do histórico — mesmo se malformado
    window.history.replaceState(window.history.state, '', window.location.pathname + window.location.search);
    if (JWT_SHAPE.test(token)) {
      write(TOKEN_KEY, token);
      write(IMPERSONATION_USER_KEY, null);
      write(ENDED_KEY, null);
    }
  }
  pageMode = read(TOKEN_KEY) !== null || read(ENDED_KEY) !== null;
}

/** Esta aba é (ou foi, e acabou) uma aba de depuração — vale até recarregar. */
export function isImpersonationPage(): boolean {
  pageMode ??= read(TOKEN_KEY) !== null || read(ENDED_KEY) !== null;
  return pageMode;
}

export function getImpersonationToken(): string | null {
  return read(TOKEN_KEY);
}

// ── estado reativo ──────────────────────────────────────────────────────────

const listeners = new Set<() => void>();
let snapshotKey = '';
let snapshot: ImpersonationState = { mode: 'none' };

function currentState(): ImpersonationState {
  if (!isImpersonationPage()) return { mode: 'none' };
  if (read(TOKEN_KEY) !== null) return { mode: 'active' };
  const ended = read(ENDED_KEY) as ImpersonationEndReason | null;
  return { mode: 'ended', reason: ended && END_REASONS.includes(ended) ? ended : 'revoked' };
}

function getSnapshot(): ImpersonationState {
  const next = currentState();
  const key = next.mode === 'ended' ? `ended:${next.reason}` : next.mode;
  if (key !== snapshotKey) {
    snapshotKey = key;
    snapshot = next;
  }
  return snapshot;
}

export function useImpersonationState(): ImpersonationState {
  return useSyncExternalStore((cb) => {
    listeners.add(cb);
    return () => listeners.delete(cb);
  }, getSnapshot);
}

/** A aba mostra o portal como um cliente, só leitura: esconda o que escreve. */
export function useIsImpersonating(): boolean {
  return useImpersonationState().mode !== 'none';
}

/**
 * Fim da sessão NESTA aba: o token e o usuário somem, e fica a marca de
 * encerrada — recarregar mostra de novo a tela de sessão encerrada, em vez de
 * virar, sem aviso, a sessão do admin.
 */
export function endImpersonationLocally(reason: ImpersonationEndReason): void {
  if (read(TOKEN_KEY) === null && read(ENDED_KEY) !== null) return; // já acabou
  write(TOKEN_KEY, null);
  write(IMPERSONATION_USER_KEY, null);
  write(ENDED_KEY, reason);
  for (const l of listeners) l();
}

/** "Voltar ao ERP": esquece a depuração e recarrega como uma aba comum. */
export function leaveImpersonation(): void {
  write(ENDED_KEY, null);
  window.location.assign('/');
}
